import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Button, Container, Select, TextInput } from '@mantine/core';
import { IconDownload, IconUpload } from '@tabler/icons-react';
import { useQuery } from '@apollo/client';

import { DragDrop } from '../../../components/file-uploads/DragDrop';
import { DashboardHeader } from '../../../components/common';
import { ImportType } from '../../../types/import';
import { GET_TEMPLATE_FILE } from '../../../apollo/queries/getTemplateFile';
import { useAuth } from '../../../context/AuthProvider';
import { checkUserRole } from '../../../utility/userRoles';
import { PRICE_SURVEY_DATA_UPLOAD_ROLES } from '../../../constants/priceSurveyUploadAccess';
import {
    SURVEY_TYPE_OPTIONS,
    SurveyType,
} from '../../../constants/surveyTypes';
import SurveyUploadHistoryTable from './SurveyUploadHistoryTable';

const SURVEY_TYPE_TO_IMPORT: Record<SurveyType, ImportType> = {
    [SurveyType.Top20]: ImportType.Top20,
    [SurveyType.Category]: ImportType.CategorySurvey,
    [SurveyType.SpecialProject]: ImportType.SpecialProjectSurvey,
};

const SURVEY_TYPE_TO_TEMPLATE: Record<SurveyType, ImportType> = {
    [SurveyType.Top20]: ImportType.Top20,
    [SurveyType.Category]: ImportType.CategorySurvey,
    [SurveyType.SpecialProject]: ImportType.SpecialProjectSurvey,
};

function PriceSurveysUploadPage() {
    const { authUser } = useAuth();
    const canAccessUpload = checkUserRole(
        authUser,
        PRICE_SURVEY_DATA_UPLOAD_ROLES,
    );

    const [surveyType, setSurveyType] = useState<SurveyType>(SurveyType.Top20);
    const [label, setLabel] = useState('');
    const [refreshKey, setRefreshKey] = useState(0);
    const [selectedTemplate, setSelectedTemplate] = useState<ImportType | null>(
        null,
    );

    const importType = SURVEY_TYPE_TO_IMPORT[surveyType];
    const labelRequired = surveyType !== SurveyType.Top20;
    const canUpload = !labelRequired || label.trim().length > 0;

    const { data, error } = useQuery(GET_TEMPLATE_FILE, {
        variables: { inputType: selectedTemplate },
        fetchPolicy: 'network-only',
        skip: !selectedTemplate,
    });

    useEffect(() => {
        if (surveyType === SurveyType.Top20) {
            setLabel('');
        }
    }, [surveyType]);

    useEffect(() => {
        if (error) {
            setSelectedTemplate(null);
            return;
        }

        if (data?.getManualImportTemplateFile) {
            const { buffer, fileName } = data.getManualImportTemplateFile;
            try {
                const byteCharacters = atob(buffer);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i += 1) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } finally {
                setSelectedTemplate(null);
            }
        }
    }, [data, error]);

    const surveyTypeSelectData = useMemo(
        () =>
            SURVEY_TYPE_OPTIONS.map(o => ({ value: o.value, label: o.label })),
        [],
    );

    if (!canAccessUpload) {
        return <Navigate to="/price-surveys" replace />;
    }

    return (
        <div className="relative pb-10">
            <DashboardHeader
                title="Price Surveys - Data upload"
                hasBreadcrumb={false}
                hasDate
            />
            <div className="px-11 w-full mb-8">
                <Container size="xl" className="rounded-lg p-4 bg-white">
                    <div className="flex items-center w-full mb-4">
                        <div className="flex items-center gap-2">
                            <IconUpload size={24} />
                            <span className="text-lg font-medium whitespace-nowrap">
                                Product list upload
                            </span>
                        </div>
                        <div className="ml-auto flex items-center">
                            <Button
                                variant="subtle"
                                leftSection={<IconDownload size={16} />}
                                onClick={() =>
                                    setSelectedTemplate(
                                        SURVEY_TYPE_TO_TEMPLATE[surveyType],
                                    )
                                }
                            >
                                Download Template
                            </Button>
                        </div>
                    </div>

                    <div
                        className={`grid grid-cols-1 gap-4 mb-6${
                            labelRequired ? ' md:grid-cols-2' : ''
                        }`}
                    >
                        <Select
                            label="Survey type"
                            data={surveyTypeSelectData}
                            value={surveyType}
                            onChange={value =>
                                setSurveyType(
                                    (value as SurveyType) || SurveyType.Top20,
                                )
                            }
                        />
                        {labelRequired && (
                            <TextInput
                                label="Label"
                                placeholder="e.g. Cement Category or Winter Promo"
                                value={label}
                                onChange={event =>
                                    setLabel(event.currentTarget.value)
                                }
                                required
                            />
                        )}
                    </div>

                    <p className="text-sm text-gray-500 mb-6">
                        Upload a product list for the selected survey type.
                        {labelRequired
                            ? ' Category and Special Project uploads require a unique label so Divisional Managers can pick the correct list when requesting a survey.'
                            : ' Top 20 uploads use the latest product list automatically.'}
                    </p>

                    <DragDrop
                        importType={importType}
                        label={label.trim() || undefined}
                        title="Click or drag and drop your file here"
                        description="Supported formats: XLSX, XLS, CSV"
                        disabled={!canUpload}
                        disabledReason={
                            labelRequired
                                ? 'Enter a label before uploading'
                                : undefined
                        }
                        onUploadComplete={() =>
                            setRefreshKey(current => current + 1)
                        }
                    />
                </Container>

                <SurveyUploadHistoryTable refreshKey={refreshKey} />
            </div>
        </div>
    );
}

export default PriceSurveysUploadPage;
